import React, { useState, useRef, useEffect } from 'react';
import { CardMedia, Dialog, DialogContent } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { StyledCard, StyledCardsContainer, StyledCardText, StyledGalleryContainer, StyledGalleryTitle, StyledGalleryWrapper, StyledLeftArrowIconButton, StyledRightArrowIconButton } from '../../../../styles/Homepage/GallerySection.style';

interface CardProps {
    name: string;
    image: string;
}

interface GalleryProps {
    title: string;
    cards: CardProps[];
}

const MobileGallerySection: React.FC<GalleryProps> = ({ title, cards }) => {
    const galleryRef = useRef<HTMLDivElement>(null);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [paused, setPaused] = useState<boolean>(false);
    const resumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const pauseScrolling = () => {
        if (resumeTimer.current) clearTimeout(resumeTimer.current);
        setPaused(true);
    };

    const resumeScrollingAfterDelay = () => {
        if (resumeTimer.current) clearTimeout(resumeTimer.current);
        resumeTimer.current = setTimeout(() => setPaused(false), 4000);
    };

    const scrollGallery = (direction: 'left' | 'right') => {
        const gallery = galleryRef.current;
        if (!gallery) return;

        const cardWidth = gallery.children[0]?.getBoundingClientRect().width || 0;
        const scrollPosition = gallery.scrollLeft;
        const galleryWidth = gallery.scrollWidth;
        const visibleWidth = gallery.offsetWidth;

        if (direction === 'left') {
            gallery.scrollBy({ left: -cardWidth, behavior: 'smooth' });
        } else {
            if (scrollPosition + visibleWidth >= galleryWidth) {
                gallery.scrollTo({ left: 0, behavior: 'smooth' });
            } else {
                gallery.scrollBy({ left: cardWidth, behavior: 'smooth' });
            }
        }
    };

    useEffect(() => {
        if (paused || modalOpen) return;

        const intervalId = setInterval(() => scrollGallery('right'), 2000);

        return () => {
            clearInterval(intervalId);
        };
    }, [paused, modalOpen]);

    useEffect(() => {
        return () => {
            if (resumeTimer.current) clearTimeout(resumeTimer.current);
        };
    }, []);

    const handleImageClick = (image: string) => {
        setSelectedImage(image);
        setModalOpen(true);
    };

    return (
        <>
            <StyledGalleryContainer>
                <StyledGalleryTitle>{title}</StyledGalleryTitle>
                <StyledGalleryWrapper>
                    {/* Left Arrow */}
                    <StyledLeftArrowIconButton
                        onClick={() => scrollGallery('left')}
                        aria-label="Scroll left"
                    >
                        <ArrowBackIos />
                    </StyledLeftArrowIconButton>

                    {/* Cards Container */}
                    <StyledCardsContainer
                        ref={galleryRef}
                        onTouchStart={pauseScrolling}
                        onTouchEnd={resumeScrollingAfterDelay}
                    >
                        {cards.map((card, index) => (
                            <StyledCard
                                key={index}
                                onClick={() => handleImageClick(card.image)}
                            >
                                <CardMedia component="img" src={card.image} alt={card.name} />
                                <StyledCardText>{card.name}</StyledCardText>
                            </StyledCard>
                        ))}
                    </StyledCardsContainer>

                    {/* Right Arrow */}
                    <StyledRightArrowIconButton
                        onClick={() => scrollGallery('right')}
                        aria-label="Scroll right"
                    >
                        <ArrowForwardIos />
                    </StyledRightArrowIconButton>
                </StyledGalleryWrapper>
            </StyledGalleryContainer>

            {/* Modal pour afficher l'image en grand */}
            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
                <DialogContent>
                    {selectedImage && (
                        <img
                            src={selectedImage}
                            alt="Full view"
                            style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MobileGallerySection;
